import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { Booking } from '../../core/models/booking.model';
import { Organisation } from '../../core/models/organisation.model';
import { Route } from '../../core/models/route.model';
import { Trip } from '../../core/models/trip.model';
import { Vehicle } from '../../core/models/vehicle.model';
import { BookingService, BookingStats } from '../../core/services/booking.service';
import { AuthService } from '../../core/services/auth.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { PassengerProfileService } from '../../core/services/passenger-profile.service';
import { RouteService } from '../../core/services/route.service';
import { TripService } from '../../core/services/trip.service';
import { VehicleService } from '../../core/services/vehicle.service';

interface DashboardStats {
  totalOrganisations: number;
  totalVehicles: number;
  totalPassengers: number;
  trackedVehicles: number;
  activeRoutes: number;
  activeTrips: number;
  todayTrips: number;
  todayBookings: number;
  totalRevenue: number;
  monthlyRevenue: number;
  upcomingTrips: number;
  pastTrips: number;
  totalSpent: number;
}

@Component({
  selector: 'app-dashboard-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard-stats.component.html',
  styleUrls: ['./dashboard-stats.component.css']
})
export class DashboardStatsComponent implements OnChanges {
  @Input() userRole!: string;
  @Input() operatorBookingEnabled = false;

  isLoading = false;
  warningMessage = '';
  lastUpdated: Date | null = null;
  stats: DashboardStats = this.createEmptyStats();

  constructor(
    private authService: AuthService,
    private organisationService: OrganisationService,
    private vehicleService: VehicleService,
    private routeService: RouteService,
    private tripService: TripService,
    private passengerProfileService: PassengerProfileService,
    private bookingService: BookingService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole'] || changes['operatorBookingEnabled']) {
      this.loadStats();
    }
  }

  loadStats(): void {
    this.isLoading = true;
    this.warningMessage = '';
    const role = normalizeUserRole(this.userRole);
    const currentUser = this.authService.getCurrentUser();
    const organisationId = role === UserRole.OPERATOR && currentUser?.organisationID
      ? currentUser.organisationID
      : undefined;

    forkJoin({
      organisations: (this.isSystemRole(role)
        ? this.organisationService.getOrganisations()
        : of([] as Organisation[])
      ).pipe(
        catchError(error => this.handleStatError<Organisation[]>('organisations', error, []))
      ),
      vehicles: (role === UserRole.PASSENGER
        ? of([] as Vehicle[])
        : this.vehicleService.getVehicles(organisationId)
      ).pipe(
        catchError(error => this.handleStatError<Vehicle[]>('vehicles', error, []))
      ),
      trips: this.tripService.getTrips(organisationId ? { organisationId } : undefined).pipe(
        catchError(error => this.handleStatError<Trip[]>('trips', error, []))
      ),
      routes: (role === UserRole.PASSENGER
        ? of([] as Route[])
        : this.routeService.getRoutes({ organisationId, includeInactive: false })
      ).pipe(
        catchError(error => this.handleStatError<Route[]>('routes', error, []))
      ),
      passengerCount: (role === UserRole.PASSENGER
        ? of(0)
        : this.passengerProfileService.getPassengerProfiles({ page: 1, perPage: 1 }).pipe(
            map(response => Number(response.meta?.total || 0))
          )
      ).pipe(
        catchError(error => this.handleStatError<number>('passengers', error, 0))
      ),
      bookings: (role === UserRole.PASSENGER || this.operatorBookingEnabled
        ? this.bookingService.getBookings()
        : of([] as Booking[])
      ).pipe(
        catchError(error => this.handleStatError<Booking[]>('bookings', error, []))
      ),
      bookingStats: (role === UserRole.PASSENGER || !this.operatorBookingEnabled
        ? of(null as BookingStats | null)
        : this.bookingService.getBookingStats()
      ).pipe(
        catchError(error => this.handleStatError<BookingStats | null>('booking stats', error, null))
      )
    }).subscribe(({ organisations, vehicles, trips, routes, passengerCount, bookings, bookingStats }) => {
      this.stats = this.calculateStats(organisations, vehicles, trips, routes, passengerCount, bookings, bookingStats);
      this.lastUpdated = new Date();
      this.isLoading = false;
    });
  }

  isPlatformEmployee(): boolean {
    return this.isSystemRole(normalizeUserRole(this.userRole));
  }

  isOperator(): boolean {
    return normalizeUserRole(this.userRole) === UserRole.OPERATOR;
  }

  isPassenger(): boolean {
    return normalizeUserRole(this.userRole) === UserRole.PASSENGER;
  }

  showBookingMetrics(): boolean {
    return !this.isPassenger() && this.operatorBookingEnabled;
  }

  private isSystemRole(role: UserRole): boolean {
    return role === UserRole.ADMIN || role === UserRole.PLATFORM_EMPLOYEE;
  }

  private calculateStats(
    organisations: Organisation[],
    vehicles: Vehicle[],
    trips: Trip[],
    routes: Route[],
    passengerCount: number,
    bookings: Booking[],
    bookingStats: BookingStats | null
  ): DashboardStats {
    const todayTrips = trips.filter(trip => this.isToday(this.getTripStart(trip)));
    const todayBookings = bookings.filter(booking => this.isToday(booking.bookingDate));
    const activeTrips = trips.filter(trip => this.isActiveTrip(trip));
    const activeRoutes = routes.filter(route => route.statusID === 1);

    return {
      totalOrganisations: organisations.length,
      totalVehicles: vehicles.length,
      totalPassengers: passengerCount,
      trackedVehicles: vehicles.filter(vehicle => !!vehicle.deviceID).length,
      activeRoutes: activeRoutes.length,
      activeTrips: activeTrips.length,
      todayTrips: todayTrips.length,
      todayBookings: bookingStats?.todayBookings ?? todayBookings.length,
      totalRevenue: bookingStats?.totalRevenue ?? this.sumRevenue(bookings),
      monthlyRevenue: bookingStats?.monthlyRevenue ?? this.sumRevenue(bookings.filter(booking => this.isCurrentMonth(booking.bookingDate))),
      upcomingTrips: trips.filter(trip => new Date(this.getTripStart(trip) || '').getTime() >= Date.now()).length,
      pastTrips: trips.filter(trip => new Date(this.getTripStart(trip) || '').getTime() < Date.now()).length,
      totalSpent: this.sumRevenue(bookings)
    };
  }

  private createEmptyStats(): DashboardStats {
    return {
      totalOrganisations: 0,
      totalVehicles: 0,
      totalPassengers: 0,
      trackedVehicles: 0,
      activeRoutes: 0,
      activeTrips: 0,
      todayTrips: 0,
      todayBookings: 0,
      totalRevenue: 0,
      monthlyRevenue: 0,
      upcomingTrips: 0,
      pastTrips: 0,
      totalSpent: 0
    };
  }

  private handleStatError<T>(label: string, error: any, fallback: T) {
    console.error(`Unable to load dashboard ${label}:`, error);
    this.warningMessage = 'Some dashboard metrics could not be loaded from the API.';
    return of(fallback);
  }

  private isToday(value: string | undefined | null): boolean {
    if (!value) {
      return false;
    }

    const date = new Date(value);
    const today = new Date();

    return date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth()
      && date.getDate() === today.getDate();
  }

  private isCurrentMonth(value: string | undefined | null): boolean {
    if (!value) {
      return false;
    }

    const date = new Date(value);
    const today = new Date();

    return date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth();
  }

  private sumRevenue(bookings: Booking[]): number {
    return bookings.reduce((total, booking) => total + Number(booking.totalPrice || 0), 0);
  }

  private getTripStart(trip: Trip): string | undefined {
    return trip.tripStartDateTime || trip.tripStartTime;
  }

  private isActiveTrip(trip: Trip): boolean {
    const status = String(trip.tripStatus || '').toLowerCase();
    return ['scheduled', 'ongoing', 'in_progress', 'active', 'delayed'].includes(status);
  }
}
